import Box from '@mui/material/Box';
import { useMemo, useState } from 'react';
import { MdClose, MdExpandMore } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROLENAME } from '../helpers/commonUses';
import CheckInOut from './CheckInOut';
import GetRolePermission from '../hooks/permission/GetRolePermission';
import GetUserPermission from '../hooks/permission/GetUserPermission';
import { useAuth } from '../hooks/useAuth';
import { NAV_ITEMS } from './navItems';

const Sidebar = ({ isOpen, setIsOpen }) => {
    const [expandedItems, setExpandedItems] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const { role } = useAuth();
    const { storePermissionPath } = GetUserPermission();
    const { storeRolePermissionPath } = GetRolePermission();
    const isSuperAdmin = role === ROLENAME;

    const hasPermission = (permissionPaths) => {
        if (!permissionPaths || permissionPaths.length === 0) return true;
        if (isSuperAdmin) return true;
        const grantedPaths = (storePermissionPath || []).concat(storeRolePermissionPath || []);
        return permissionPaths.some((permissionPath) => grantedPaths.includes(permissionPath));
    };

    // SidebarOld.js hides the "by supervisor" entries from Super Admin and gates
    // everything else on the user + role permission route lists.
    const isVisible = (entry) => {
        if (entry.superAdminOnly) return isSuperAdmin;
        if (entry.hideForSuperAdmin && isSuperAdmin) return false;
        return hasPermission(entry.permissionPaths);
    };

    const navItems = useMemo(() => {
        return NAV_ITEMS.filter(isVisible)
            .map((item) => {
                if (!item.subItems) return item;
                const subItems = item.subItems
                    .filter((subItem) => (subItem.isGroup ? true : isVisible(subItem)))
                    .map((subItem) => {
                        if (!subItem.isGroup) return subItem;
                        return { ...subItem, subItems: subItem.subItems.filter(isVisible) };
                    })
                    .filter((subItem) => !subItem.isGroup || subItem.subItems.length > 0);
                return { ...item, subItems };
            })
            .filter((item) => item.path || (item.subItems && item.subItems.length > 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin, storePermissionPath, storeRolePermissionPath]);

    const toggleExpand = (label) => {
        setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    const toggleGroup = (key) => {
        setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleNavClick = (path) => {
        if (path) {
            navigate(path);
            if (window.innerWidth < 1024) {
                setIsOpen(false);
            }
        }
    };

    const isItemActive = (item) => {
        if (item.path && location.pathname.startsWith(item.path)) return true;
        if (item.subItems) {
            return item.subItems.some((sub) => {
                if (sub.isGroup && sub.subItems) {
                    return sub.subItems.some((s) => location.pathname.startsWith(s.path));
                }
                return sub.path && location.pathname.startsWith(sub.path);
            });
        }
        return false;
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <Box
                    onClick={() => setIsOpen(false)}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 40,
                        backdropFilter: 'blur(4px)',
                        '@media (min-width:1024px)': { display: 'none' },
                    }}
                />
            )}

            {/* Sidebar */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 50,
                    width: '16rem',
                    bgcolor: '#ffffff',
                    borderRight: '1px solid #e2e8f0',
                    transition: 'transform 300ms ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: '1rem',
                    overflow: 'hidden',
                    transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
                }}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        px: '1.5rem',
                        mb: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Box
                            sx={{
                                width: '2rem',
                                height: '2rem',
                                borderRadius: '0.5rem',
                                bgcolor: '#2563eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '1.125rem',
                            }}
                        >
                            T
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                letterSpacing: '-0.025em',
                            }}
                        >
                            Team Up.
                        </Box>
                    </Box>
                    <Box
                        component="button"
                        onClick={() => setIsOpen(false)}
                        aria-label="Close sidebar"
                        sx={{
                            display: 'block',
                            '@media (min-width:1024px)': { display: 'none' },
                            p: '0.25rem',
                            color: '#64748b',
                            borderRadius: '0.5rem',
                            transition: 'background-color 150ms',
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                    >
                        <MdClose size={20} />
                    </Box>
                </Box>

                {/* Check In / Check Out */}
                <Box sx={{ '&:not(:empty)': { mb: '1rem' } }}>
                    <CheckInOut />
                </Box>

                {/* Navigation Menu */}
                <Box
                    component="nav"
                    className="sidebar-scroll"
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: '1rem',
                        pb: '1rem',
                        '& > *:not(:first-of-type)': { mt: '0.25rem' },
                    }}
                >
                    {navItems.map((item) => {
                        const isActive = isItemActive(item);
                        const isExpanded = expandedItems[item.label];
                        const hasSubItems = item.subItems && item.subItems.length > 0;

                        return (
                            <Box key={item.label}>
                                <Box
                                    component="button"
                                    onClick={() => {
                                        if (hasSubItems) {
                                            toggleExpand(item.label);
                                        } else {
                                            handleNavClick(item.path);
                                        }
                                    }}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: '0.75rem',
                                        py: '0.625rem',
                                        borderRadius: '0.75rem',
                                        transition: 'all 200ms',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontFamily: 'inherit',
                                        bgcolor: isActive ? '#eff6ff' : 'transparent',
                                        color: isActive ? '#1d4ed8' : '#475569',
                                        '&:hover': isActive
                                            ? {}
                                            : { bgcolor: '#f8fafc', color: '#0f172a' },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <item.icon
                                            size={18}
                                            style={{ color: isActive ? '#2563eb' : '#94a3b8' }}
                                        />
                                        {item.label}
                                    </Box>
                                    {hasSubItems && (
                                        <MdExpandMore
                                            size={16}
                                            style={{
                                                color: '#94a3b8',
                                                transition: 'transform 200ms',
                                                transform: isExpanded ? 'rotate(180deg)' : 'none',
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Sub Items */}
                                {hasSubItems && isExpanded && (
                                    <Box
                                        sx={{
                                            pl: '2.25rem',
                                            pr: '0.75rem',
                                            py: '0.25rem',
                                            '& > *:not(:first-of-type)': { mt: '0.25rem' },
                                        }}
                                    >
                                        {item.subItems.map((subItem) => {
                                            if (subItem.isGroup) {
                                                const groupKey = `${item.label}__${subItem.label}`;
                                                const isGroupOpen = expandedGroups[groupKey];
                                                const isGroupActive = subItem.subItems?.some((s) =>
                                                    location.pathname.startsWith(s.path)
                                                );
                                                return (
                                                    <Box key={subItem.label}>
                                                        <Box
                                                            component="button"
                                                            onClick={() => toggleGroup(groupKey)}
                                                            sx={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                px: '0.75rem',
                                                                py: '0.5rem',
                                                                fontSize: '0.875rem',
                                                                fontWeight: 600,
                                                                borderRadius: '0.5rem',
                                                                transition: 'background-color 150ms, color 150ms',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                fontFamily: 'inherit',
                                                                bgcolor: isGroupActive
                                                                    ? 'rgba(239, 246, 255, 0.3)'
                                                                    : 'transparent',
                                                                color: isGroupActive ? '#1d4ed8' : '#475569',
                                                                '&:hover': isGroupActive
                                                                    ? {}
                                                                    : { color: '#0f172a', bgcolor: '#f8fafc' },
                                                            }}
                                                        >
                                                            {subItem.label}
                                                            <MdExpandMore
                                                                size={14}
                                                                style={{
                                                                    color: '#94a3b8',
                                                                    transition: 'transform 200ms',
                                                                    transform: isGroupOpen
                                                                        ? 'rotate(180deg)'
                                                                        : 'none',
                                                                }}
                                                            />
                                                        </Box>
                                                        {isGroupOpen && (
                                                            <Box
                                                                sx={{
                                                                    pl: '0.75rem',
                                                                    '& > *:not(:first-of-type)': {
                                                                        mt: '0.125rem',
                                                                    },
                                                                }}
                                                            >
                                                                {subItem.subItems.map((leaf) => {
                                                                    const isLeafActive =
                                                                        location.pathname.startsWith(
                                                                            leaf.path
                                                                        );
                                                                    return (
                                                                        <Box
                                                                            component="button"
                                                                            key={leaf.label}
                                                                            onClick={() =>
                                                                                handleNavClick(
                                                                                    leaf.path
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                width: '100%',
                                                                                display: 'block',
                                                                                textAlign: 'left',
                                                                                px: '0.75rem',
                                                                                py: '0.375rem',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 500,
                                                                                borderRadius: '0.5rem',
                                                                                transition:
                                                                                    'background-color 150ms, color 150ms',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                fontFamily: 'inherit',
                                                                                bgcolor: isLeafActive
                                                                                    ? 'rgba(239, 246, 255, 0.5)'
                                                                                    : 'transparent',
                                                                                color: isLeafActive
                                                                                    ? '#1d4ed8'
                                                                                    : '#94a3b8',
                                                                                '&:hover': isLeafActive
                                                                                    ? {}
                                                                                    : {
                                                                                          color: '#1e293b',
                                                                                          bgcolor: '#f8fafc',
                                                                                      },
                                                                            }}
                                                                        >
                                                                            {leaf.label}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            }
                                            const isChildActive = location.pathname.startsWith(
                                                subItem.path
                                            );
                                            return (
                                                <Box
                                                    component="button"
                                                    key={subItem.label}
                                                    onClick={() => handleNavClick(subItem.path)}
                                                    sx={{
                                                        width: '100%',
                                                        display: 'block',
                                                        textAlign: 'left',
                                                        px: '0.75rem',
                                                        py: '0.5rem',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        borderRadius: '0.5rem',
                                                        transition: 'background-color 150ms, color 150ms',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        fontFamily: 'inherit',
                                                        bgcolor: isChildActive
                                                            ? 'rgba(239, 246, 255, 0.5)'
                                                            : 'transparent',
                                                        color: isChildActive ? '#1d4ed8' : '#64748b',
                                                        '&:hover': isChildActive
                                                            ? {}
                                                            : { color: '#0f172a', bgcolor: '#f8fafc' },
                                                    }}
                                                >
                                                    {subItem.label}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default Sidebar;
